<template>
  <div class="min-h-screen bg-gray-50">
    <AlertLayout title="Reminders!" message="-Kindly remember to inform the donor on the RISKS and BENEFITS of donation.<br>-Kindly remember to as well seek the donor's consent for future recalls in the event of emergencies.<br>-Kindly inform donor that this registration guarantees them an online account to the Damu-Sasa Blood Donor's portal.<br>-Donors Phone number will be used for communications." type="warning" />

    <!-- Header -->
    <div class="px-8 py-5 bg-white border-b border-gray-200">
      <div class="flex items-center gap-4">
        <button class="text-gray-400 transition hover:text-gray-600" @click="$emit('back')" aria-label="Go back">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 class="text-xl font-bold text-gray-900">New Apheresis Donation</h1>
          <p class="text-sm text-gray-400">Fill in the required pre-donation information</p>
        </div>
      </div>
    </div>

    <div class="px-8 py-8 mx-auto max-w-8xl">
      <h2 class="mb-4 text-sm font-semibold text-gray-800">New Donor Information</h2>

      <!-- Section 1: Arm Inspection -->
      <section class="p-6 mb-6 bg-white border border-gray-200 rounded-xl">
        <div class="flex items-center gap-2 mb-5">
          <span class="flex items-center justify-center w-6 h-6 text-xs font-semibold text-blue-600 bg-blue-100 rounded-full">1</span>
          <h3 class="text-base font-semibold text-gray-900">Arm Inspection</h3>
        </div>

        <div>
          <label class="block mb-1.5 text-sm text-gray-600">Vein Visibility</label>
          <select
            v-model="formData.veinVisibility"
            class="w-full max-w-md px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
          >
            <option value="" disabled selected>Select visibility</option>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
          </select>
        </div>
      </section>

      <!-- Section 2: Pre-donation Vitals -->
      <section class="p-6 mb-6 bg-white border border-gray-200 rounded-xl">
        <div class="flex items-center gap-2 mb-5">
          <span class="flex items-center justify-center w-6 h-6 text-xs font-semibold text-blue-600 bg-blue-100 rounded-full">2</span>
          <h3 class="text-base font-semibold text-gray-900">Pre-donation Vitals</h3>
        </div>

        <div class="grid grid-cols-1 gap-5 mb-5 md:grid-cols-3">
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Weight (kg)</label>
            <input
              v-model="formData.weight"
              type="number"
              step="0.1"
              placeholder="0.0"
              :class="inputClass('weight')"
            />
          </div>
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Height (cm)</label>
            <input
              v-model="formData.height"
              type="number"
              placeholder="0"
              :class="inputClass('height')"
            />
          </div>
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Temperature (°C)</label>
            <input
              v-model="formData.temperature"
              type="number"
              step="0.1"
              placeholder="37.0"
              :class="inputClass('temperature')"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Pulse (bpm)</label>
            <input
              v-model="formData.pulse"
              type="number"
              placeholder="72"
              :class="inputClass('pulse')"
            />
          </div>
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Hemoglobin (HB) g/dL</label>
            <input
              v-model="formData.hemoglobin"
              type="number"
              step="0.1"
              placeholder="14.0"
              :class="inputClass('hemoglobin')"
            />
          </div>
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Blood Pressure (mmHg)</label>
            <input
              v-model="formData.bloodPressure"
              type="text"
              placeholder="120/80"
              :class="inputClass('bloodPressure')"
            />
          </div>
        </div>
      </section>

      <!-- Section 3: Screening & Sample Details -->
      <section class="p-6 mb-6 bg-white border border-gray-200 rounded-xl">
        <div class="flex items-center gap-2 mb-5">
          <span class="flex items-center justify-center w-6 h-6 text-xs font-semibold text-blue-600 bg-blue-100 rounded-full">3</span>
          <h3 class="text-base font-semibold text-gray-900">Screening &amp; Sample Details</h3>
        </div>

        <div class="grid grid-cols-1 gap-5 mb-6 md:grid-cols-3">
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Blood Sample - Donor Name</label>
            <input
              v-model="formData.sampleDonorName"
              type="text"
              placeholder="Enter full name"
              :class="inputClass('sampleDonorName')"
              readonly
            />
          </div>
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Platelet Pre-count</label>
            <input
              v-model="formData.plateletPreCount"
              type="number"
              placeholder="e.g., 250"
              :class="inputClass('plateletPreCount')"
            />
          </div>
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Red Blood Cells Pre-count</label>
            <input
              v-model="formData.totalBloodCountPreCount"
              type="number"
              step="0.1"
              placeholder="e.g., 5.0"
              :class="inputClass('totalBloodCountPreCount')"
            />
          </div>
        </div>

        <div class="mb-2">
          <p class="mb-3 text-sm text-gray-600">Blood Screening TTI (Transfusion Transmitted Infections)</p>
          <div class="grid grid-cols-2 gap-5 mb-6 sm:grid-cols-3 md:grid-cols-5">
            <div v-for="test in ttiTests" :key="test.key">
              <label class="block mb-1.5 text-sm text-gray-600">{{ test.label }}</label>
              <select
                v-model="formData.tti[test.key]"
                :class="inputClass(`tti.${test.key}`)"
              >
                <option value="" disabled selected></option>
                <option value="negative">Negative</option>
                <option value="positive">Positive</option>
              </select>
            </div>
          </div>
        </div>

        <hr class="mb-6 border-gray-100" />

        <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Blood Group</label>
            <select
              v-model="formData.bloodGroup"
              class="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
            >
              <option value="" disabled selected>Select blood group</option>
              <option v-for="bg in bloodGroups" :key="bg.blood_group_id || bg.id" :value="bg.blood_group_id || bg.id">
                {{ bg.description }}
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-1.5 text-sm text-gray-600">Expected Yield</label>
            <div class="relative">
              <input
                v-model="formData.expectedYield"
                type="number"
                step="0.1"
                placeholder="0.0"
                class="w-full py-2.5 pl-4 pr-16 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
              />
              <span class="absolute text-sm text-gray-400 -translate-y-1/2 right-4 top-1/2">units</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 4: Donor Recall -->
      <section class="p-6 mb-6 bg-white border border-gray-200 rounded-xl">
        <div class="flex items-center gap-2 mb-1">
          <span class="flex items-center justify-center w-6 h-6 text-xs font-semibold text-blue-600 bg-blue-100 rounded-full">4</span>
          <h3 class="text-base font-semibold text-gray-900">Donor Recall</h3>
        </div>
        <p class="mb-4 ml-8 text-sm text-gray-400">How should this donor be contacted once screening results are ready?</p>

        <div class="inline-flex items-center gap-2 px-4 py-2 mb-5 rounded-lg bg-orange-50">
          <span class="text-sm font-medium text-gray-700">Recall method:</span>
          <span class="px-2 py-0.5 text-xs font-semibold text-orange-500 bg-orange-100 rounded">SMS</span>
        </div>

        <div class="mb-5">
          <label class="block mb-1.5 text-sm text-gray-600">Phone Number</label>
          <input
            v-model="formData.phoneNumber"
            type="tel"
            placeholder="Phone number will be auto-populated from donor record"
            class="w-full max-w-md px-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
          />
          <p class="mt-1.5 text-xs text-gray-400">Auto-populated from donor record. SMS will be sent when results are ready.</p>
        </div>

        <div class="mb-3">
          <label class="block mb-1.5 text-sm text-gray-600">SMS Message</label>
          <textarea
            v-model="formData.smsMessage"
            rows="3"
            readonly
            class="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg resize-none bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
          ></textarea>
          <p class="mt-1.5 text-xs text-gray-400">This message will be sent to the donor when results are ready.</p>
        </div>

        <label class="flex items-center gap-2 mb-5 text-sm text-gray-600 cursor-pointer">
          <input
            v-model="formData.smsConsent"
            type="checkbox"
            class="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-300"
          />
          Donor has consented to receive SMS notifications about screening results and future donation opportunities.
        </label>
      </section>

      <!-- Footer actions -->
      <div class="flex items-center justify-between pt-2">
        <button
          type="button"
          class="px-6 py-2.5 text-sm font-semibold text-orange-500 border border-orange-500 rounded-lg hover:bg-orange-50 transition"
          @click="clearVitalsForm"
        >
          Cancel
        </button>
        <button
          type="button"
          class="px-6 py-2.5 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition"
          @click="validateAndSave('continue')"
        >
          Save Record
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import SweetAlertService from "@/services/SweetAlertService";
import AlertLayout from "@/components/AlertLayout.vue";

export default {
  name: "NewApheresisScreeningForm",

  components: {
    AlertLayout,
  },

  props: {
    donor_id: {
      type: String,
      required: true,
    },
  },

  emits: ["back", "saved"],

  data() {
    return {
      isLoading: false,
      invalidFields: {},
      bloodGroups: [],
      ttiTests: [
        { key: "hiv", label: "HIV" },
        { key: "hbv", label: "HBV" },
        { key: "hcv", label: "HCV" },
        { key: "syphilis", label: "Syphilis" },
        { key: "malaria", label: "Malaria" },
      ],
      formData: {
        veinVisibility: "",
        weight: "",
        height: "",
        temperature: "",
        pulse: "",
        hemoglobin: "",
        bloodPressure: "",
        sampleDonorName: "",
        plateletPreCount: "",
        totalBloodCountPreCount: "",
        tti: {
          hiv: "",
          hbv: "",
          hcv: "",
          syphilis: "",
          malaria: "",
        },
        bloodGroup: "",
        expectedYield: "",
        phoneNumber: "",
        smsMessage:
          "Hello, your recent apheresis donation screening results are ready. Please visit the facility at your earliest convenience. Thank you.",
        smsConsent: false,
      },
    };
  },

  async created() {
    await Promise.all([
      this.fetchBloodGroups(),
      this.fetchDonorDetails()
    ]);
  },

  methods: {
    async fetchBloodGroups() {
      try {
        const response = await api.getBloodGroups();
        this.bloodGroups = response.data || response;
      } catch (error) {
        console.error("Error fetching blood groups:", error);
      }
    },

    async fetchDonorDetails() {
       try {
         const donorId = this.donor_id || this.$route?.params?.donor_id;
         if (!donorId) { console.error("No donor ID available"); return; }
         const response = await api.getDonor(donorId);
         const donor = response.data || response;
         if (donor?.user?.first_name && donor?.user?.last_name) {
           this.formData.sampleDonorName = `${donor.user.first_name} ${donor.user.last_name}`;
         }
         if (donor?.user?.phone_number) {
           this.formData.phoneNumber = donor.user.phone_number;
         }
       } catch (error) {
         console.error("Error fetching donor details:", error);
       }
     },

    inputClass(field) {
      return [
        "w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300",
        this.invalidFields[field] ? "border-red-400 bg-red-50" : "border-gray-200",
      ];
    },

    isValidBloodPressure(value) {
      return /^\d{2,3}\/\d{2,3}$/.test((value || "").trim());
    },

    checkEmptyFields() {
      const emptyFields = [];
      const requiredTop = [
        "veinVisibility",
        "weight",
        "pulse",
        "hemoglobin",
        "bloodPressure",
        "plateletPreCount",
        "totalBloodCountPreCount",
        "bloodGroup",
        "expectedYield",
      ];

      requiredTop.forEach((key) => {
        const value = this.formData[key];
        if (!value || !value.toString().trim()) {
          emptyFields.push(key);
        }
      });

      Object.entries(this.formData.tti).forEach(([key, value]) => {
        if (!value) emptyFields.push(`tti.${key}`);
      });

      return emptyFields;
    },

    validateAndSave(action) {
      this.invalidFields = {};
      const emptyFields = this.checkEmptyFields();

      if (emptyFields.length > 0) {
        emptyFields.forEach((f) => (this.invalidFields[f] = true));
        SweetAlertService.showEmptyFieldsAlert(emptyFields);
        return;
      }

      if (!this.isValidBloodPressure(this.formData.bloodPressure)) {
        this.invalidFields.bloodPressure = true;
        SweetAlertService.showError(
          "Invalid Input",
          "Please enter blood pressure in the correct format (e.g., 120/80)."
        );
        return;
      }

      if (action === "continue") {
        this.handleSaveAndContinue();
      } else {
        this.handleSaveAndDefer();
      }
    },

    async handleSaveAndContinue() {
      await this.submitVitals();
    },

    async handleSaveAndDefer() {
      await this.submitVitals();
    },

    async submitVitals() {
      this.isLoading = true;
      try {
        const vitalsResponse = await api.createDonorVital(this.donor_id, {
            arm_inspection: this.formData.veinVisibility,
            weight: this.formData.weight,
            height: this.formData.height,
            temperature: this.formData.temperature,
            pulse: this.formData.pulse,
            hb_levels: this.formData.hemoglobin,
            blood_pressure: this.formData.bloodPressure,
            platelets_pre_count: this.formData.plateletPreCount,
            red_blood_cells_pre_count: this.formData.totalBloodCountPreCount,
            sms_consent: this.formData.smsConsent,
            phone_number: this.formData.phoneNumber,
            sms_message: this.formData.smsMessage,
          });
        console.log("Donor vitals saved:", vitalsResponse);

        const apheresisResponse = await api.createApheresisScreening(this.donor_id, {
          blood_group_id: this.formData.bloodGroup,
          hiv: this.formData.tti.hiv,
          hbv: this.formData.tti.hbv,
          hcv: this.formData.tti.hcv,
          syphilis: this.formData.tti.syphilis,
          malaria: this.formData.tti.malaria,
          expected_yield: this.formData.expectedYield,
        });
        console.log("Apheresis screening saved:", apheresisResponse);

        await SweetAlertService.showSuccess(
          "Success!",
          "Apheresis screening added successfully."
        );

        this.$emit("saved", { vitals: vitalsResponse, apheresis: apheresisResponse });

        // Only redirect to donation form if status is Complete (not Deferred)
        if (apheresisResponse?.status !== "Deferred") {
          this.$router.push({
            name: 'NewRecordDonationForm',
            params: { donor_id: this.donor_id },
            query: { from_apheresis: 'true' }
          });
        } else {
          this.$router.push({ name: 'DonorManagementPage' });
        }
      } catch (error) {
        console.error("Error submitting the form data:", error);
        const errorMsg = error?.message || "Something went wrong while saving records.";
        SweetAlertService.showError(
          "Error",
          errorMsg
        );
      } finally {
        this.isLoading = false;
      }
    },

    clearVitalsForm() {
      this.formData = {
        veinVisibility: "",
        weight: "",
        height: "",
        temperature: "",
        pulse: "",
        hemoglobin: "",
        bloodPressure: "",
        sampleDonorName: "",
        plateletPreCount: "",
        totalBloodCountPreCount: "",
        tti: { hiv: "", hbv: "", hcv: "", syphilis: "", malaria: "" },
        bloodGroup: "",
        expectedYield: "",
        phoneNumber: "",
        smsMessage:
          "Hello, your recent apheresis donation screening results are ready. Please visit the facility at your earliest convenience. Thank you.",
        smsConsent: false,
      };
      this.invalidFields = {};
    },
  },
};
</script>

<style scoped>
select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%239CA3AF'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1.1em;
}
</style>